/**
 * CommitDialog — modal for creating a commit on the current branch.
 *
 * Shows a summary of uncommitted changes and a message input.
 */

import { useState } from "react";
import { Badge, Button, Dialog, Flex, Text, TextArea } from "@radix-ui/themes";

import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useCreateCommit, useUncommitted } from "../../hooks/useVersioning";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommitDialog({ open, onOpenChange }: Props) {
  const { projectId, currentBranch } = useProvenanceContext();
  const { data: uncommitted } = useUncommitted(projectId, currentBranch, open);
  const commitMutation = useCreateCommit(projectId);
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  const total = uncommitted?.total ?? 0;

  function handleCommit() {
    const trimmed = message.trim();
    if (!trimmed) {
      setError("Commit message is required");
      return;
    }

    commitMutation.mutate(
      { branch: currentBranch, message: trimmed },
      {
        onSuccess: () => {
          setMessage("");
          setError(null);
          onOpenChange(false);
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : String(err));
        },
      },
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="480px">
        <Dialog.Title>Create Commit</Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Save a snapshot of the current state on{" "}
          <strong>{currentBranch}</strong>.
        </Dialog.Description>

        <Flex direction="column" gap="3" mt="4">
          {currentBranch === "main" && total > 0 && (
            <Text size="1" color="amber" style={{ fontStyle: "italic" }}>
              Tip: consider creating a branch first to keep main clean.
            </Text>
          )}

          {uncommitted && (
            <Flex gap="2" wrap="wrap">
              <Badge color="green" size="1">{uncommitted.inserts} added</Badge>
              <Badge color="yellow" size="1">{uncommitted.updates} modified</Badge>
              <Badge color="red" size="1">{uncommitted.deletes} deleted</Badge>
              <Text size="1" color="gray">({total} uncommitted change{total !== 1 ? "s" : ""})</Text>
            </Flex>
          )}

          <TextArea
            placeholder="Describe what changed..."
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              setError(null);
            }}
            rows={3}
          />

          {error && (
            <Text size="1" color="red">{error}</Text>
          )}
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">Cancel</Button>
          </Dialog.Close>
          <Button
            onClick={handleCommit}
            disabled={commitMutation.isPending || total === 0}
          >
            {commitMutation.isPending ? "Committing..." : "Commit"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
